'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  WorkerOutgoingMessage,
  WorkerProgressMessage,
  WorkerCompleteMessage,
  WorkerErrorMessage,
  WorkerInfoMessage,
  WorkerFrameProcessedMessage,
  EncodeConfig,
} from './workers/video-encoder.worker.types';

export interface UseWebCodecsExportState {
  isExporting: boolean;
  progress: number; // 0-100
  currentFrame: number;
  totalFrames: number;
  error: string | null;
  encodedFrames: number;
  estimatedTimeRemaining: number; // seconds
  exportSpeed: number; // fps
  isSupported: boolean;
  statusMessage: string;
  phase: 'idle' | 'encoding' | 'saving' | 'converting' | 'done';
  selectedFormat: string; // Display format like "H.264 MP4", "H.264 MOV", "ProRes MOV"
}

export type OutputFormat = 'MP4' | 'MOV' | 'ProRes' | 'WebM';

export interface UseWebCodecsExportOptions {
  duration?: number; // ms, default 3000
  fps?: number; // default 60
  outputFormat?: OutputFormat; // default 'MP4'
  loopDuration?: number; // Loop duration in seconds (3, 5, or 10) for filename
  fpsForFilename?: number; // FPS value for filename display (e.g., 50, 60)
}

// Fixed high quality bitrate (50 Mbps)
const FIXED_BITRATE = 50_000_000;

/**
 * Check if running in Tauri environment
 * Uses Tauri v2 reliable detection methods
 */
function isTauriEnvironment(): boolean {
  if (typeof window === 'undefined') return false;

  // Method 1: Check for __TAURI_IPC__ - the most reliable indicator for Tauri
  // This is present even during `tauri dev` mode
  if ((window as any).__TAURI_IPC__ !== undefined) return true;

  // Method 2: Check for window.__TAURI__ (sometimes available)
  if ((window as any).__TAURI__) return true;

  // Method 3: Check navigator userAgent for 'Tauri'
  if (typeof navigator !== 'undefined' && navigator.userAgent.includes('Tauri')) return true;

  return false;
}

/**
 * Get display name for output format
 */
function getFormatDisplayName(format: OutputFormat): string {
  switch (format) {
    case 'MP4':
      return 'H.264 MP4';
    case 'MOV':
      return 'H.264 MOV';
    case 'ProRes':
      return 'ProRes MOV';
    case 'WebM':
      return 'VP9 WebM';
  }
}

/**
 * Check if WebCodecs API is supported
 */
function checkWebCodecsSupport(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(
    globalThis.VideoEncoder &&
    globalThis.VideoFrame &&
    globalThis.ImageData
  );
}

/**
 * Download a blob via browser
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Main hook for WebCodecs GPU-native export
 * Renders frames on main thread using the actual drawFunction,
 * then sends them to a worker for GPU encoding.
 */
export function useWebCodecsVideoExport(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  drawFunction: (time: number, showSelection: boolean) => void
) {
  const [exportState, setExportState] = useState<UseWebCodecsExportState>({
    isExporting: false,
    progress: 0,
    currentFrame: 0,
    totalFrames: 0,
    error: null,
    encodedFrames: 0,
    estimatedTimeRemaining: 0,
    exportSpeed: 0,
    isSupported: false,
    statusMessage: '',
    phase: 'idle',
    selectedFormat: 'H.264 MP4',
  });

  const workerRef = useRef<Worker | null>(null);
  const startTimeRef = useRef<number>(0);
  const abortRef = useRef<boolean>(false);
  const containerFormatRef = useRef<'mp4' | 'webm'>('mp4');
  const outputFormatRef = useRef<OutputFormat>('MP4');
  const lastProgressRef = useRef<number>(0); // Track last progress to ensure monotonic increase
  const durationRef = useRef<number>(3000); // Store duration for FFmpeg progress
  const loopDurationRef = useRef<number | undefined>(undefined); // Store loop duration for filename
  const fpsForFilenameRef = useRef<number | undefined>(undefined); // Store fps for filename
  const ffmpegListenerRef = useRef<(() => void) | null>(null);
  const isTauriRef = useRef<boolean>(false);

  /**
   * Update progress - ensures it only goes up, never down
   */
  const updateProgress = useCallback((newProgress: number, statusMessage: string, phase: UseWebCodecsExportState['phase']) => {
    // Ensure progress only increases
    const safeProgress = Math.max(lastProgressRef.current, Math.min(100, newProgress));
    lastProgressRef.current = safeProgress;
    
    setExportState(prev => ({
      ...prev,
      progress: safeProgress,
      statusMessage,
      phase,
    }));
  }, []);

  /**
   * Close export and reset state
   */
  const closeExport = useCallback(() => {
    // Cleanup FFmpeg listener
    if (ffmpegListenerRef.current) {
      ffmpegListenerRef.current();
      ffmpegListenerRef.current = null;
    }
    
    lastProgressRef.current = 0;
    setExportState(prev => ({
      ...prev,
      isExporting: false,
      progress: 0,
      phase: 'idle',
    }));
  }, []);

  /**
   * Check WebCodecs support and Tauri environment on mount
   */
  useEffect(() => {
    const isSupported = checkWebCodecsSupport();
    isTauriRef.current = isTauriEnvironment();
    setExportState(prev => ({ ...prev, isSupported }));
    
    if (!isSupported) {
      // console.warn('[WebCodecs Export] WebCodecs API not supported in this browser');
    }
  }, []);

  /**
   * Initialize web worker
   */
  useEffect(() => {
    if (!checkWebCodecsSupport()) return;

    try {
      workerRef.current = new Worker(
        new URL('./workers/video-encoder.worker.ts', import.meta.url),
        { type: 'module' }
      );
    } catch (error) {
      // console.error('[WebCodecs Export] Failed to create worker:', error);
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      // Cleanup FFmpeg listener on unmount
      if (ffmpegListenerRef.current) {
        ffmpegListenerRef.current();
        ffmpegListenerRef.current = null;
      }
    };
  }, []);

  /**
   * Setup FFmpeg progress listener (Tauri only)
   */
  const setupFfmpegProgressListener = useCallback(async () => {
    if (!isTauriRef.current) return;
    
    try {
      // Dynamic import for Tauri APIs
      const { listen } = await import('@tauri-apps/api/event');
      
      // Cleanup previous listener if any
      if (ffmpegListenerRef.current) {
        ffmpegListenerRef.current();
      }
      
      // Listen for FFmpeg progress events
      const unlisten = await listen<{ progress: number; time_ms: number; speed: string }>(
        'ffmpeg-progress',
        (event) => {
          const { progress } = event.payload;
          // Map FFmpeg progress (0-1) to our range (85-100)
          const mappedProgress = 85 + (progress * 15);
          
          if (mappedProgress > lastProgressRef.current) {
            lastProgressRef.current = mappedProgress;
            setExportState(prev => ({
              ...prev,
              progress: mappedProgress,
            }));
          }
        }
      );
      
      ffmpegListenerRef.current = unlisten;
    } catch (error) {
      // console.warn('[WebCodecs Export] Failed to setup FFmpeg progress listener:', error);
    }
  }, []);

  /**
   * Handle export completion - Web version (browser download)
   */
  const handleWebExportComplete = useCallback(
    async (videoBuffer: ArrayBuffer, fileSize: number, totalTimeMs: number) => {
      try {
        const sourceFormat = containerFormatRef.current;
        const formatDisplayName = getFormatDisplayName('WebM');
        
        // Update to saving phase
        updateProgress(90, '', 'saving');
        
        // Get canvas dimensions for filename
        const canvas = canvasRef.current;
        const width = canvas?.width || 1920;
        const height = canvas?.height || 1080;
        
        // Create blob and download
        const mimeType = sourceFormat === 'webm' ? 'video/webm' : 'video/mp4';
        const extension = sourceFormat === 'webm' ? 'webm' : 'mp4';
        const blob = new Blob([videoBuffer], { type: mimeType });
        const loopSuffix = loopDurationRef.current ? `_${loopDurationRef.current}s` : '';
        const fpsSuffix = fpsForFilenameRef.current ? `@${fpsForFilenameRef.current}Hz` : '';
        const filename = `${width}x${height}_VisualGrid_Loop${loopSuffix}${fpsSuffix}.${extension}`;

        downloadBlob(blob, filename);
        
        // Complete!
        const avgFps = exportState.totalFrames / (totalTimeMs / 1000);
        
        updateProgress(100, formatDisplayName, 'done');

        // Keep window open - user must close it manually
        setExportState(prev => ({
          ...prev,
          progress: 100,
          phase: 'done',
          statusMessage: formatDisplayName,
        }));

      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        // console.error('[WebCodecs Export] Failed to download file:', error);
        lastProgressRef.current = 0;
        setExportState(prev => ({
          ...prev,
          isExporting: false,
          progress: 0,
          error: `Erreur: ${error.message}`,
          statusMessage: `Erreur: ${error.message}`,
          phase: 'idle',
        }));
      }
    },
    [canvasRef, exportState.totalFrames, updateProgress]
  );

  /**
   * Handle export completion - Tauri version (native save + FFmpeg)
   */
  const handleTauriExportComplete = useCallback(
    async (videoBuffer: ArrayBuffer, fileSize: number, totalTimeMs: number) => {
      try {
        // Dynamic imports for Tauri APIs
        const { save } = await import('@tauri-apps/plugin-dialog');
        const { invoke } = await import('@tauri-apps/api/core');
        
        const targetFormat = outputFormatRef.current;
        const sourceFormat = containerFormatRef.current;
        const formatDisplayName = getFormatDisplayName(targetFormat);
        
        // Update to saving phase (80%)
        updateProgress(80, '', 'saving');
        
        // Show save dialog
        const canvas = canvasRef.current;
        const width = canvas?.width || 1920;
        const height = canvas?.height || 1080;
        
        // Determine file extension
        let extension = 'mp4';
        let filterName = 'MP4 Video';
        if (targetFormat === 'MOV') {
          extension = 'mov';
          filterName = 'QuickTime Video';
        } else if (targetFormat === 'ProRes') {
          extension = 'mov';
          filterName = 'ProRes Video';
        } else if (targetFormat === 'WebM') {
          extension = 'webm';
          filterName = 'WebM Video';
        }

        const loopSuffix = loopDurationRef.current ? `_${loopDurationRef.current}s` : '';
        const fpsSuffix = fpsForFilenameRef.current ? `@${fpsForFilenameRef.current}Hz` : '';
        const filePath = await save({
          defaultPath: `${width}x${height}_VisualGrid_Loop${loopSuffix}${fpsSuffix}.${extension}`,
          filters: [
            { name: filterName, extensions: [extension] },
            { name: 'All Files', extensions: ['*'] },
          ],
        });

        if (!filePath) {
          // User cancelled - reset everything
          lastProgressRef.current = 0;
          setExportState(prev => ({
            ...prev,
            isExporting: false,
            progress: 0,
            statusMessage: '',
            phase: 'idle',
          }));
          return;
        }

        // Convert ArrayBuffer to Base64 for Tauri transmission
        updateProgress(82, '', 'converting');

        const uint8Array = new Uint8Array(videoBuffer);
        
        // Use chunks to avoid call stack size exceeded for large buffers
        const chunkSize = 65536;
        let binaryString = '';
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          binaryString += String.fromCharCode.apply(null, Array.from(chunk));
        }
        const base64 = btoa(binaryString);

        // Check if we need FFmpeg conversion
        const needsConversion = sourceFormat === 'webm' || (targetFormat !== 'MP4' && targetFormat !== 'WebM');

        if (needsConversion && targetFormat !== 'WebM') {
          updateProgress(85, formatDisplayName, 'converting');

          // Setup FFmpeg progress listener before starting conversion
          await setupFfmpegProgressListener();

          try {
            await invoke('save_and_convert_video', {
              buffer: base64,
              outputPath: filePath,
              format: targetFormat,
              sourceFormat: sourceFormat,
              durationMs: durationRef.current,
            });
          } finally {
            // Cleanup listener
            if (ffmpegListenerRef.current) {
              ffmpegListenerRef.current();
              ffmpegListenerRef.current = null;
            }
          }
          
          updateProgress(100, formatDisplayName, 'done');
        } else {
          updateProgress(90, formatDisplayName, 'converting');

          await invoke('save_video_buffer', {
            filePath,
            buffer: base64,
          });
          
          updateProgress(100, formatDisplayName, 'done');
        }

        // Keep window open - user must close it manually
        setExportState(prev => ({
          ...prev,
          progress: 100,
          phase: 'done',
          statusMessage: formatDisplayName,
        }));

      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        // console.error('[WebCodecs Export] Failed to save/convert file:', error);
        lastProgressRef.current = 0;
        setExportState(prev => ({
          ...prev,
          isExporting: false,
          progress: 0,
          error: `Erreur: ${error.message}`,
          statusMessage: `Erreur: ${error.message}`,
          phase: 'idle',
        }));
      }
    },
    [canvasRef, exportState.totalFrames, updateProgress, setupFfmpegProgressListener]
  );

  /**
   * Handle export completion - routes to Web or Tauri handler
   */
  const handleExportComplete = useCallback(
    async (videoBuffer: ArrayBuffer, fileSize: number, totalTimeMs: number) => {
      if (isTauriRef.current) {
        await handleTauriExportComplete(videoBuffer, fileSize, totalTimeMs);
      } else {
        await handleWebExportComplete(videoBuffer, fileSize, totalTimeMs);
      }
    },
    [handleTauriExportComplete, handleWebExportComplete]
  );

  /**
   * Start video export
   */
  const startExport = useCallback(
    async (options: UseWebCodecsExportOptions = {}) => {
      const {
        duration = 3000,
        fps = 60,
        outputFormat = 'MP4',
        loopDuration,
        fpsForFilename,
      } = options;

      // Store the desired output format and duration
      outputFormatRef.current = outputFormat;
      durationRef.current = duration;
      loopDurationRef.current = loopDuration;
      fpsForFilenameRef.current = fpsForFilename;
      const formatDisplayName = getFormatDisplayName(outputFormat);
      
      // Reset progress tracking
      lastProgressRef.current = 0;

      // Check support
      if (!exportState.isSupported) {
        setExportState(prev => ({
          ...prev,
          error: 'WebCodecs API not supported in this browser',
        }));
        return { success: false, error: 'WebCodecs not supported' };
      }

      const canvas = canvasRef.current;
      if (!canvas) {
        setExportState(prev => ({ ...prev, error: 'Canvas not available' }));
        return { success: false, error: 'Canvas not available' };
      }

      const worker = workerRef.current;
      if (!worker) {
        setExportState(prev => ({ ...prev, error: 'Worker not initialized' }));
        return { success: false, error: 'Worker not initialized' };
      }

      const { width, height } = canvas;
      const totalFrames = Math.floor((duration / 1000) * fps);
      const frameDuration = 1000 / fps;

      abortRef.current = false;

      setExportState({
        isExporting: true,
        progress: 0,
        currentFrame: 0,
        totalFrames,
        error: null,
        encodedFrames: 0,
        estimatedTimeRemaining: 0,
        exportSpeed: 0,
        isSupported: true,
        statusMessage: '',
        phase: 'encoding',
        selectedFormat: formatDisplayName,
      });

      startTimeRef.current = performance.now();

      return new Promise<{ success: boolean; error?: string }>((resolve) => {
        // Set up worker message handler
        worker.onmessage = async (event: MessageEvent) => {
          const msg = event.data as WorkerOutgoingMessage | { type: 'ready'; containerFormat?: string };

          switch (msg.type) {
            case 'ready': {
              // Store container format info
              const readyMsg = msg as { type: 'ready'; containerFormat?: 'mp4' | 'webm' };
              containerFormatRef.current = readyMsg.containerFormat || 'mp4';
              
              // Encoder is ready, start sending frames
              updateProgress(2, '', 'encoding');
              
              // Faire un rendu initial pour s'assurer que tout est initialisé
              // Cela garantit que les canvas du sonar et autres éléments sont prêts
              // Utiliser un temps très petit (1ms) pour initialiser sans affecter l'animation
              drawFunction(1, false);
              
              // Attendre un peu pour que le rendu soit complété
              await new Promise(resolve => setTimeout(resolve, 10));
              
              try {
                await renderAndSendFrames(
                  canvas,
                  worker,
                  drawFunction,
                  totalFrames,
                  fps,
                  frameDuration
                );
              } catch (err) {
                const error = err instanceof Error ? err.message : String(err);
                lastProgressRef.current = 0;
                setExportState(prev => ({
                  ...prev,
                  isExporting: false,
                  error,
                  statusMessage: `Erreur: ${error}`,
                  phase: 'idle',
                }));
                resolve({ success: false, error });
              }
              break;
            }

            case 'progress': {
              const progressMsg = msg as WorkerProgressMessage;
              // Progress for encoding phase: 2% to 78%
              const encodingProgress = 2 + (progressMsg.currentFrame / totalFrames) * 76;
              const elapsed = performance.now() - startTimeRef.current;
              const elapsedSecs = elapsed / 1000;
              const currentFps = progressMsg.currentFrame / Math.max(elapsedSecs, 0.1);
              const remaining = (totalFrames - progressMsg.currentFrame) / Math.max(currentFps, 1);

              // Only update if progress increased
              if (encodingProgress > lastProgressRef.current) {
                lastProgressRef.current = encodingProgress;
                setExportState(prev => ({
                  ...prev,
                  progress: encodingProgress,
                  currentFrame: progressMsg.currentFrame,
                  encodedFrames: progressMsg.encodedFrames,
                  estimatedTimeRemaining: remaining,
                  exportSpeed: currentFps,
                  statusMessage: '',
                  phase: 'encoding',
                }));
              }
              break;
            }

            case 'complete': {
              const completeMsg = msg as WorkerCompleteMessage;
              updateProgress(80, '', 'encoding');
              await handleExportComplete(
                completeMsg.videoBuffer,
                completeMsg.fileSize,
                completeMsg.totalTimeMs
              );
              resolve({ success: true });
              break;
            }

            case 'error': {
              const errorMsg = msg as WorkerErrorMessage;
              // console.error('[WebCodecs Export] Error:', errorMsg.error);
              lastProgressRef.current = 0;
              setExportState(prev => ({
                ...prev,
                isExporting: false,
                error: errorMsg.error,
                statusMessage: `Erreur: ${errorMsg.error}`,
                phase: 'idle',
              }));
              resolve({ success: false, error: errorMsg.error });
              break;
            }

            case 'info': {
              // Info messages are silently ignored in production
              break;
            }

            case 'frame_processed': {
              const processedMsg = msg as WorkerFrameProcessedMessage;
              // This is handled in renderAndSendFrames via the backpressure system
              break;
            }
          }
        };

        worker.onerror = (error) => {
          // console.error('[WebCodecs Export] Worker error:', error);
          lastProgressRef.current = 0;
          setExportState(prev => ({
            ...prev,
            isExporting: false,
            error: `Worker error: ${error.message}`,
            phase: 'idle',
          }));
          resolve({ success: false, error: error.message });
        };

        // Initialize encoder in worker
        const config: EncodeConfig = {
          fps,
          duration_ms: duration,
          width,
          height,
          bitrate: FIXED_BITRATE,
          keyframeInterval: 60,
        };

        worker.postMessage({ type: 'init', config });
      });

      /**
       * Render frames on main thread and send to worker
       * Implements backpressure control to prevent memory overflow
       */
      async function renderAndSendFrames(
        canvas: HTMLCanvasElement,
        worker: Worker,
        drawFn: (time: number, showSelection: boolean) => void,
        totalFrames: number,
        fps: number,
        frameDuration: number
      ) {
        // Backpressure control: limit frames in transit based on resolution
        // For large resolutions (>2K), use smaller buffer to avoid memory issues
        const frameSizeBytes = canvas.width * canvas.height * 4;
        const frameSizeMB = (frameSizeBytes / 1024 / 1024);
        const maxFramesInTransit = frameSizeMB > 30 ? 3 : frameSizeMB > 15 ? 5 : 10;
        let framesInTransit = 0;
        const frameProcessingResolvers = new Map<number, () => void>();
        
        // Handler for frame_processed messages - must be set before sending frames
        const originalOnMessage = worker.onmessage;
        worker.onmessage = (event: MessageEvent) => {
          const msg = event.data;
          if (msg.type === 'frame_processed') {
            const processedMsg = msg as WorkerFrameProcessedMessage;
            framesInTransit = Math.max(0, framesInTransit - 1);
            const resolver = frameProcessingResolvers.get(processedMsg.frameIndex);
            if (resolver) {
              resolver();
              frameProcessingResolvers.delete(processedMsg.frameIndex);
            }
          } else if (originalOnMessage) {
            originalOnMessage.call(worker, event);
          }
        };
        
        for (let i = 0; i < totalFrames; i++) {
          if (abortRef.current) {
            worker.postMessage({ type: 'cancel' });
            return;
          }

          // Backpressure: wait if too many frames in transit
          while (framesInTransit >= maxFramesInTransit) {
            await new Promise(resolve => setTimeout(resolve, 5));
          }

          const time = i * frameDuration;

          // Render frame using the actual drawFunction
          drawFn(time, false);

          // Create ImageBitmap from canvas (can be transferred to worker)
          const imageBitmap = await createImageBitmap(canvas);

          // Send frame to worker for encoding (transfer the bitmap for zero-copy)
          framesInTransit++;
          const frameProcessedPromise = new Promise<void>((resolve) => {
            frameProcessingResolvers.set(i, resolve);
          });
          
          worker.postMessage(
            {
              type: 'frame',
              frameIndex: i,
              imageBitmap: imageBitmap,
              timestamp: time,
            },
            [imageBitmap] // Transfer the bitmap
          );

          // Update progress locally (smoother updates)
          const frameProgress = 2 + ((i + 1) / totalFrames) * 76;
          const elapsed = performance.now() - startTimeRef.current;
          const elapsedSecs = elapsed / 1000;
          const currentFps = (i + 1) / Math.max(elapsedSecs, 0.1);
          const remaining = (totalFrames - i - 1) / Math.max(currentFps, 1);

          // Only update if progress increased significantly (avoid too many updates)
          if (frameProgress - lastProgressRef.current >= 1) {
            lastProgressRef.current = frameProgress;
            setExportState(prev => ({
              ...prev,
              progress: frameProgress,
              currentFrame: i + 1,
              estimatedTimeRemaining: remaining,
              exportSpeed: currentFps,
              statusMessage: '',
            }));
          }

          // Yield to allow UI updates (every 10 frames)
          if (i % 10 === 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }

        // Wait for all frames to be processed before finalizing
        let waitIterations = 0;
        const maxWaitIterations = 3000; // 30 seconds max wait (3000 * 10ms)
        while (framesInTransit > 0) {
          await new Promise(resolve => setTimeout(resolve, 10));
          waitIterations++;
          
          // Safety timeout
          if (waitIterations >= maxWaitIterations) {
            throw new Error(`Export timeout: ${framesInTransit} frames still in transit after 30 seconds`);
          }
        }

        // Tell worker to finalize
        updateProgress(78, '', 'encoding');
        worker.postMessage({ type: 'finalize' });
      }
    },
    [canvasRef, drawFunction, exportState.isSupported, handleExportComplete, updateProgress]
  );

  /**
   * Cancel export
   */
  const cancelExport = useCallback(() => {
    abortRef.current = true;
    
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'cancel' });
    }

    // Cleanup FFmpeg listener
    if (ffmpegListenerRef.current) {
      ffmpegListenerRef.current();
      ffmpegListenerRef.current = null;
    }

    lastProgressRef.current = 0;
    setExportState(prev => ({
      ...prev,
      isExporting: false,
      progress: 0,
      currentFrame: 0,
      totalFrames: 0,
      error: null,
      encodedFrames: 0,
      estimatedTimeRemaining: 0,
      exportSpeed: 0,
      statusMessage: '',
      phase: 'idle',
    }));
  }, []);

  return {
    ...exportState,
    startExport,
    cancelExport,
    closeExport,
  };
}
